const LOCALE = 'en-GB'

const time = new Intl.DateTimeFormat(LOCALE, { hour: '2-digit', minute: '2-digit' })
const day = new Intl.DateTimeFormat(LOCALE, { day: 'numeric', month: 'long', year: 'numeric' })
const dateTime = new Intl.DateTimeFormat(LOCALE, {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
})

/**
 * Time of day, as in `21:47`.
 * @param date - The moment, as a `Date` or an ISO string.
 * @returns The formatted time, in the browser's time zone.
 */
export function formatTime(date: Date | string): string {
  return time.format(new Date(date))
}

/**
 * A day, as in `12 October 2026`.
 * @param date - The moment, as a `Date` or an ISO string.
 * @returns The formatted date.
 */
export function formatDay(date: Date | string): string {
  return day.format(new Date(date))
}

/**
 * A day and a time, as in `20 October 2026 at 14:00`.
 * @param date - The moment, as a `Date` or an ISO string.
 * @returns The formatted date and time.
 */
export function formatDateTime(date: Date | string): string {
  return dateTime.format(new Date(date))
}

/**
 * Whether two moments fall on the same calendar day, in the browser's time zone.
 * @param a - First moment.
 * @param b - Second moment.
 * @returns `true` on the same day.
 */
export function isSameDay(a: Date | string, b: Date | string): boolean {
  return new Date(a).toDateString() === new Date(b).toDateString()
}

/**
 * Label of a day in the chat: `today`, `yesterday`, or the date.
 * @param date - Any moment of the day.
 * @param labels - Words for today and yesterday.
 * @param labels.today - Label of the current day.
 * @param labels.yesterday - Label of the day before.
 * @param now - The current moment, for tests.
 * @returns The label.
 */
export function dayLabel(
  date: Date | string,
  labels: { today: string; yesterday: string },
  now: Date = new Date(),
): string {
  if (isSameDay(date, now)) return labels.today
  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  if (isSameDay(date, yesterday)) return labels.yesterday
  return formatDay(date)
}
